import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { combineLatest, Subject } from 'rxjs';

import { Router } from '@angular/router';
import { NgScrollbar } from 'ngx-scrollbar';
import { toSignal } from '@angular/core/rxjs-interop';
import { ContractorsApiService } from '../../../../../services/contractors-api.service';
import { NotificationsService } from '../../../../../services/notifications.service';
import { OPTIONS } from '../../../../../framework/constants/options-list.constants';
import { DriveService } from '../../../../../services/drive.service';
import { MessagesStateService } from '../../../../../services/messages-state.service';
import { PageLoadingComponent } from '../../../../../framework/page-loading/page-loading.component';
import {
  COMMITMENTS_TYPE,
  INTERACTION_BAR_STATES,
} from '../../../../../framework/constants/interaction-bar.constants';
import { InteractionBarStateService } from '../../../../../services/interaction-bar-state.service';
import { viewProjectActions } from '../../../../../store/view-project/view-project.actions';
import { CurrentUserService } from '../../../../../services/current-user.service';
import { BidProposalComponent } from '../bid-proposal/bid-proposal.component';
import { MoneyPipe } from '../../../../../pipes/framework/money-short.pipe';
import { commitmentsSelectors } from '../../../../../store/commitments/commitments.selectors';
import {
  CONTRACTOR_TYPE,
  convertUserToAccessibleUser,
  InvitedSubcontractor,
  USER_MANAGER,
} from '../../../../../framework/constants/user.constants';
import { viewProjectSelectors } from '../../../../../store/view-project/view-project.selectors';
import { ProjectApiService } from '../../../../../services/project-api.service';
import {
  CONTRACTOR_TYPES,
  MANAGE_PROJECT_TABS,
  PROJECT_VIEWS,
} from '../../../../../framework/constants/view-project.constants';
import { SPEND_TYPES } from '../../../../../framework/constants/spend.constants';
import { TooltipDirective } from '@app/directives/tooltip.directive';
import { BID_STATUS } from 'src/app/framework/constants/project.constants';
import { primeSelectors } from '@app/store/prime-commitments/prime-commitments.selectors';
import { ChangeActionsStore } from '@app/store/change-actions/change-actions.store';
import { InvitationsStore } from '@app/store/invitations/invitations.store';
import { StyledOptionsListComponent } from '@app/framework/overlays/styled-options-list/styled-options-list.component';
import { IStyledOption } from '@app/framework/constants/documents.constants';

@Component({
  selector: 'app-manage-project',
  imports: [
    CommonModule,
    StyledOptionsListComponent,
    PageLoadingComponent,
    BidProposalComponent,
    MoneyPipe,
    TooltipDirective,
    NgScrollbar,
  ],
  templateUrl: './manage-project.component.html',
  styleUrl: './manage-project.component.scss',
})
export class ManageProjectComponent implements OnInit, OnDestroy {
  tabs = MANAGE_PROJECT_TABS;
  selectedTab: MANAGE_PROJECT_TABS;
  isLoading = true;
  projectId$ = this.store.select(viewProjectSelectors.selectProjectId);
  projectId: number;
  CONTRACTOR_TYPES = CONTRACTOR_TYPES;
  lineItems = toSignal(this.store.select(viewProjectSelectors.selectLineItemsSummary));
  projectHasApprovedContract = toSignal(
    this.store.select(commitmentsSelectors.selectProjectHasApprovedContract),
  );
  selectHasPrimeContract$ = this.store.select(primeSelectors.selectHasPrimeContract);
  CORs = this.changeActionsStore.cors;

  protected readonly SPEND_TYPES = SPEND_TYPES;
  protected readonly USER_MANAGER = USER_MANAGER;

  contractorsByType: Record<CONTRACTOR_TYPE, InvitedSubcontractor[]> = {
    [CONTRACTOR_TYPE.CONTRACTOR]: [],
    [CONTRACTOR_TYPE.ARCHITECT]: [],
    [CONTRACTOR_TYPE.SUBCONTRACTOR]: [],
    [CONTRACTOR_TYPE.INSPECTOR]: [],
    [CONTRACTOR_TYPE.ENGINEER]: [],
    [CONTRACTOR_TYPE.MATERIAL_SUPPLIER]: [],
    [CONTRACTOR_TYPE.CONSULTANT]: [],
    [CONTRACTOR_TYPE.GENERAL_CONTRACTOR]: [],
  };
  // key: contractorId
  itemOptions: Record<number, IStyledOption[]> = {};
  disabledOptions: Record<number, OPTIONS[]> = {};

  BID_STATUS = BID_STATUS;

  COMMITMENTS_TYPE = COMMITMENTS_TYPE;
  INTERACTION_BAR_STATES = INTERACTION_BAR_STATES;
  NO_BUDGET = 'Please make sure the project has a budget to enable this option.';
  NO_PRIME_CONTRACT = 'Please make sure the project has a Prime Contract to enable this option.';
  NO_SUBCONTRACT =
    'Please make sure the project has at least one approved contract to enable this option.';
  NO_COR = 'Please make sure the project has at least one approved COR to enable this option.';

  isDestroyed$ = new Subject<boolean>();
  constructor(
    private projectApi: ProjectApiService,
    private store: Store,
    private contractorService: ContractorsApiService,
    private notif: NotificationsService,
    private router: Router,
    private driveService: DriveService,
    private messagesStateService: MessagesStateService,
    private interactionBarService: InteractionBarStateService,
    public userService: CurrentUserService,
    private changeActionsStore: ChangeActionsStore,
    private invitationsStore: InvitationsStore,
  ) {}

  ngOnInit() {
    this.loadContractors();

    combineLatest([this.interactionBarService.announceState, this.userService.data$])
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe(([state, user]) => {
        if (state?.action !== INTERACTION_BAR_STATES.MANAGE_PROJECT) {
          return;
        }
        if (state?.data?.tab && state?.data?.tab in MANAGE_PROJECT_TABS) {
          this.selectedTab = state.data.tab;
          return;
        }

        this.selectedTab = user?.isPaidAccount
          ? MANAGE_PROJECT_TABS.SERVICE_TEAM
          : MANAGE_PROJECT_TABS.COMMITMENTS;
      });
  }

  setSelectedTab(tab: MANAGE_PROJECT_TABS) {
    this.selectedTab = tab;
  }
  loadContractors() {
    this.isLoading = true;

    this.projectId$
      .pipe(
        takeUntil(this.isDestroyed$),
        tap((id) => {
          this.projectId = id;
        }),
        switchMap((projectId) => this.projectApi.getInvitedContractors(projectId)),
      )
      .subscribe({
        next: (invitedContractors) => {
          this.isLoading = false;

          console.log('Invited contractors', invitedContractors);

          if (!invitedContractors) {
            return;
          }
          this.clearContractorsList();

          for (const contractor of invitedContractors) {
            // only type if backend won't change back
            if (!contractor.type_id) {
              continue;
            }
            this.contractorsByType[contractor.type_id].push(contractor);
            this.setContractorOptions(contractor);
          }
        },
        error: (err) => {
          this.notif.showError('Error loading contractors');
          console.log(err);
          this.isLoading = false;
        },
      });
  }

  private clearContractorsList() {
    for (const key of Object.keys(this.contractorsByType)) {
      this.contractorsByType[key] = [];
    }
  }

  private setContractorOptions(contractor: InvitedSubcontractor) {
    const options: IStyledOption[] = [
      { value: OPTIONS.VIEW_CONTRACT },
      { value: OPTIONS.MESSAGE },
      { value: OPTIONS.DISMISS },
    ];
    const disabledOptions: OPTIONS[] = [];

    if (!contractor.contract) {
      disabledOptions.push(OPTIONS.VIEW_CONTRACT);
    }

    this.itemOptions[contractor.id] = options;
    this.disabledOptions[contractor.id] = disabledOptions;
  }

  isContractorsListEmpty() {
    for (const contractorList of Object.values(this.contractorsByType)) {
      if (contractorList.length !== 0) {
        return false;
      }
    }
    return true;
  }

  async registerItemOption(option: OPTIONS, contractor: InvitedSubcontractor) {
    switch (option) {
      case OPTIONS.VIEW_CONTRACT: {
        // todo: navigate to the specific contract
        console.log('View contract for contractor', contractor);
        this.router.navigate(['webapp', 'projects', this.projectId, PROJECT_VIEWS.COMMITMENTS]);
        break;
      }
      case OPTIONS.MESSAGE: {
        this.messagesStateService.newMessageTo(convertUserToAccessibleUser(contractor));
        break;
      }
      case OPTIONS.DISMISS: {
        const resp = await this.notif.showPopup('Dismiss service provider?');
        if (!resp) {
          return;
        }
        if (!contractor.invitation_id) {
          this.notif.showError('Invite not found!');
          return;
        }

        this.invitationsStore.cancelSentSubcontractInvitation(contractor.invitation_id);

        break;
      }
    }
  }

  addCommitment(type: INTERACTION_BAR_STATES) {
    this.interactionBarService.openCommitments(type);
  }

  openForecastModifications() {
    this.interactionBarService.openForecastModifications();
  }

  /**
   * Open the spend view after closing the interaction bar with a delay (looks better)
   */
  openCashflowAllocation() {
    this.interactionBarService.close();
    this.notif.showLoading();
    setTimeout(() => {
      // setTimeout is needed to avoid freezing the UI
      this.store.dispatch(
        viewProjectActions.viewChanged({ view: PROJECT_VIEWS.BUDGET_ALLOCATION }),
      );

      // not sure why this dispatch is needed, the changeBudgetType() should do it...
      // this is needed otherwise the view won't change
      // this.store.dispatch(setSelectedSpendType({ spendType: budgetType }));
    }, 500);
  }

  openBudgetAdjustment() {
    this.interactionBarService.openBudgetAdjustment({
      projectId: this.projectId,
      lineItems: this.lineItems(),
    });
  }

  /**
   * Open the history page after closing the interaction bar with a delay(looks better)
   */
  setHistoryView() {
    this.interactionBarService.close();
    setTimeout(() => {
      this.store.dispatch(viewProjectActions.viewChanged({ view: PROJECT_VIEWS.BUDGET_HISTORY }));
    }, 500);
  }

  addProposal() {
    this.selectedTab = MANAGE_PROJECT_TABS.PROPOSAL;
  }

  ngOnDestroy() {
    this.isDestroyed$.next(true);
    this.isDestroyed$.complete();
  }

  openAddEditPrimeContract() {
    this.selectHasPrimeContract$.pipe(takeUntil(this.isDestroyed$)).subscribe((hasContract) => {
      if (hasContract) {
        this.interactionBarService.editPrimeContract(this.projectId);
        return;
      }
      this.interactionBarService.addPrimeContract();
    });
  }

  openAddPrimeChangeOrder() {
    this.interactionBarService.openInteractionBar(INTERACTION_BAR_STATES.PRIME_CHANGE_ORDER);
  }

  openAddPrimeInvoice() {
    this.interactionBarService.openPrimeInvoice();
  }

  openAddAnticipatedIncome() {
    this.interactionBarService.openInteractionBar(INTERACTION_BAR_STATES.ANTICIPATED_INCOME);
  }

  initiateChangeAction() {
    this.interactionBarService.openChangeAction(false);
  }
}
